import {
  Application,
  ApplicationRefereeRelation,
  EnglishTestType,
  GradeLevel,
  GradeSystem,
  IReferee,
  IStudy,
  ResultStatus,
} from 'types';
import { formatYearMonthDate } from './dateHelpers';

type FormattedText = {
  linkText?: string;
  text?: string;
  subtext?: string;
};

// Helper to get short results type label for display
const getResultsTypeLabel = (resultStatus: ResultStatus): string => {
  switch (resultStatus) {
    case ResultStatus.FinalResults:
      return 'Final';
    case ResultStatus.InterimResults:
      return 'Predicted';
    case ResultStatus.NoResults:
      return 'Pending';
    default:
      return 'Unknown';
  }
};

// Helper to find the resultStatus for a qualification by matching its qualificationTypeRowId to qualificationTypeSelections
const getResultStatusForQualification = (
  qual: { qualificationTypeRowId?: string },
  qualificationTypeSelections: IStudy['qualificationTypeSelections'],
  fallbackResultStatus?: ResultStatus,
): ResultStatus | undefined => {
  // Try to find matching selection by qualificationTypeRowId → selection.id
  const matchingSelection = qualificationTypeSelections?.find(sel => sel.id === qual.qualificationTypeRowId);
  return matchingSelection?.resultStatus ?? fallbackResultStatus;
};

// Format academic results from a study history object (used for offer requirement fulfilment data).
// Always returns an array for easier consumption - empty array if no data.
//
// Two display formats are used depending on the academic requirement type:
//   - Single academic requirement (isMultiQualification=false):
//       Detailed format — "{qualType} in {subject} (Grade {grade} / {gradeSystemLabel})"
//       with institution/country/status subtext per qualification.
//   - Multiple academic requirements (isMultiQualification=true):
//       Compact format — "{grade} in {subject} ({qualType})" with no subtext.
//       The institution context is already visible from the qualification group heading.
export const getAcademicResultsFromStudyHistory = (
  studyHistory: IStudy | undefined,
  isMultiQualification = false,
): Array<FormattedText> => {
  if (!studyHistory) {
    return [];
  }
  const { institutionName, qualifications, country, resultStatus, qualificationTypeSelections } = studyHistory;
  const baseSubtext = `${institutionName || 'Unknown institution'}, ${country || 'Unknown country'}`;

  // Sort qualifications by key (KSUID) to ensure chronological order (oldest first)
  const sortedQualifications = Object.entries(qualifications ?? {})
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([, qual]) => qual);

  if (sortedQualifications.length === 0) {
    return [{ text: 'No qualifications provided', subtext: baseSubtext }];
  }

  // Display all qualifications from the highest study with their details
  return sortedQualifications.map((qual, index) => {
    // Find the resultStatus for this specific qualification's group
    const qualResultStatus = getResultStatusForQualification(qual, qualificationTypeSelections, resultStatus);

    const qualificationLabel =
      qual?.qualificationTypeLabel ||
      qual?.otherQualificationName ||
      qual?.levelLabel ||
      (qual?.level && GradeLevel[qual.level]) ||
      qual?.level ||
      'Unknown qualification';
    const grade = qual?.grade || 'N/A';

    // Multi-qualification academic requirements: compact grade-first format, no subtext
    if (isMultiQualification) {
      const text = qual?.subject
        ? `${grade} in ${qual.subject} (${qualificationLabel})`
        : `${grade} (${qualificationLabel})`;
      return { text };
    }

    // Single academic requirement: detailed qualification-type-first format
    // Institution subtext shown only on the last qualification so it appears at the bottom of the list
    const isLast = index === sortedQualifications.length - 1;
    const subtext = isLast ? baseSubtext : undefined;
    const gradeSystemLabel =
      qual?.gradeSystemLabel ||
      (qual?.gradeSystem && GradeSystem[qual.gradeSystem as keyof typeof GradeSystem]) ||
      qual?.gradeSystem ||
      'N/A';
    const displayLabel = qual?.subject ? `${qualificationLabel} in ${qual.subject}` : qualificationLabel;
    const isPending = qualResultStatus === ResultStatus.NoResults;
    const gradeDisplay = isPending ? `Pending / ${gradeSystemLabel}` : `${grade} / ${gradeSystemLabel}`;
    const text = `${displayLabel} (Grade ${gradeDisplay})`;
    return { text, subtext };
  });
};

export interface IEnglishTestData {
  hasTakenTest?: boolean;
  // Fields when test was taken
  date?: string;
  referenceNo?: string;
  score?: string;
  type?: string;
  typeOther?: string;
  validUntil?: string;
  // Fields when test was not taken
  reasonNotReqd?: string;
  reasonNotReqdOther?: string;
}

export const getEnglishLanguageResultsFromApplication = (
  englishTestData: IEnglishTestData,
  application: Application,
): FormattedText => {
  if (englishTestData.hasTakenTest === false) {
    // Test not taken - display reason with qualification details if available
    const text = englishTestData.reasonNotReqd ?? '';
    let subtext = englishTestData.reasonNotReqdOther || '';

    // If reason is PrevQual and we have application data, look up the qualification
    if (!subtext && application?.englishQualification && text.includes('qualification')) {
      const allStudies = {
        highest: application.studyHistory?.highest,
        ...(application.studyHistory?.previous ?? {}),
      };

      const qualKey = application.englishQualification;
      const foundQualification =
        Object.values(allStudies).find(study => study?.qualifications?.[qualKey])?.qualifications?.[qualKey] ?? null;

      if (foundQualification) {
        const qualificationLabel =
          foundQualification?.qualificationTypeLabel ||
          foundQualification?.otherQualificationName ||
          foundQualification?.levelLabel ||
          (foundQualification?.level && GradeLevel[foundQualification.level]) ||
          foundQualification?.level ||
          'Unknown qualification';
        subtext = `${qualificationLabel}${foundQualification?.subject ? ` in ${foundQualification.subject}` : ''}`;
      }
    }

    return { text, subtext };
  }
  // Test taken - display test results
  if (englishTestData.type === EnglishTestType.Other) {
    return { text: `${englishTestData.typeOther} ${englishTestData.score}` };
  }
  return { text: `${englishTestData.type} ${englishTestData.score}` };
};

// Helper function to get the qualification level label from YHS data
const getQualificationLevelLabel = (highest: Application['studyHistory']['highest']): string | undefined => {
  if (!highest) return undefined;

  // Try to get from qualificationTypeSelections first (new YHS structure)
  if (highest.qualificationTypeSelections && highest.qualificationTypeSelections.length > 0) {
    const firstSelection = highest.qualificationTypeSelections[0];
    // If it's "Other", use the custom name
    if (firstSelection.qualificationTypeLabel === 'Other' && firstSelection.customQualificationTypeName) {
      return firstSelection.customQualificationTypeName;
    }
    // Otherwise use the label
    if (firstSelection.qualificationTypeLabel) {
      return firstSelection.qualificationTypeLabel;
    }
  }

  // Try to get from first qualification (fallback)
  if (highest.qualifications) {
    const firstQual = Object.values(highest.qualifications)[0];
    if (firstQual?.qualificationTypeLabel) {
      return firstQual.qualificationTypeLabel;
    }
    if (firstQual?.otherQualificationName) {
      return firstQual.otherQualificationName;
    }
  }

  // Final fallback to level field (old structure)
  return highest.level;
};

export const getAcademicTranscriptFromApplication = (application: Application): FormattedText | undefined => {
  if (!application?.studyHistory?.highest?.academicTranscriptDocument) {
    return undefined;
  }
  const { institutionName, resultStatus } = application.studyHistory.highest;
  const levelLabel = getQualificationLevelLabel(application.studyHistory.highest);
  const resultsType = getResultsTypeLabel(resultStatus);
  const text = `${institutionName || 'Unknown institution'} ${
    levelLabel || 'Unknown level'
  } transcript (${resultsType})`;
  return { text };
};

export const getEnglishLanguageCertificateFromApplication = (application: Application): FormattedText => {
  if (application.hasEnglishTest === false) {
    let subtext = '';
    const text = application.reasonNotReqd ?? '';
    if (application.reasonNotReqdOther) {
      subtext = application.reasonNotReqdOther;
    } else if (application.englishQualification) {
      // Search for the qualification across all studies (highest and previous)
      const allStudies = {
        highest: application.studyHistory?.highest,
        ...(application.studyHistory?.previous ?? {}),
      };

      const qualKey = application.englishQualification;
      const foundQualification =
        Object.values(allStudies).find(study => study?.qualifications?.[qualKey])?.qualifications?.[qualKey] ?? null;

      if (foundQualification) {
        // Prefer new YHS fields (qualificationTypeLabel) over old fields (level)
        const qualificationLabel =
          foundQualification?.qualificationTypeLabel ||
          foundQualification?.otherQualificationName ||
          foundQualification?.levelLabel ||
          (foundQualification?.level && GradeLevel[foundQualification.level]) ||
          foundQualification?.level ||
          'Unknown qualification';
        subtext = `${qualificationLabel}${foundQualification?.subject ? ` in ${foundQualification.subject}` : ''}`;
      }
    }
    return { text, subtext };
  }
  let text = '';

  if (application.englishTest.hasTakenTest) {
    if (application.englishTest?.type) {
      if (application.englishTest.type === EnglishTestType.Other) {
        text = `Other ${application.englishTest.typeOther || ''} results`;
      } else {
        text = `${application.englishTest.type || 'English test'} results`;
      }
    }
  }

  if (!text) {
    text = 'English test results';
  }

  return { linkText: text };
};

export const getProofOfManagerialExperienceFromApplication = (
  application: Application,
): FormattedText[] | undefined => {
  if (!application?.employmentHistory?.length) {
    return undefined;
  }
  return application.employmentHistory.map(({ jobTitle, employerName, startDate, endDate, type }) => {
    const text = `${jobTitle || 'Unknown job'} at ${employerName || 'Unknown employer'} /n [${type || 'Unknown type'}]`;
    const subtext = `${startDate ? formatYearMonthDate(startDate) : 'Unknown'} - ${
      endDate ? formatYearMonthDate(endDate) : 'Unknown'
    }`;
    return {
      text,
      subtext,
    };
  });
};

export const getRefereeRelationshipText = (referee: IReferee): string => {
  if (referee.relationship === ApplicationRefereeRelation.Other && referee.otherRelationshipDetails) {
    return referee.otherRelationshipDetails;
  }
  return referee.relationship;
};

// TODO above this to say it should be refactored once we have tsc compiling
// function to safely get properties from unknown fulfilmentData without TypeScript errors (fulfilmentData has many different shapes, depending on the requirement)
export const getFulfilmentProp = (obj: unknown, prop: string): unknown =>
  obj && typeof obj === 'object' && prop in obj ? (obj as Record<string, unknown>)[prop] : undefined;
